// WHAT: React Context wrapper for notification unread count
// WHY:  Provides simplified access to notification unread count from admin domain
// DOMAIN: Shared → Contexts
// DEPENDENCIES: useNotifications hook from admin domain
// RISKS:
//   - NOTE: Creates dependency on admin domain - may cause circular dependencies
//   - NOTE: Very minimal wrapper - may need additional notification features

import type { ReactNode } from "react";

import { useNotifications } from "@/domains/admin/notifications/hooks/useNotifications";

import { NotificationContext } from "./NotificationContext.shared";

/**
 * WHAT:
 *   Provider component that wraps the admin domain's useNotifications hook
 *   to provide unread count to shared components.
 *
 * WHY:
 *   Provides a simplified interface for accessing notification state without
 *   directly depending on the admin domain in shared components.
 *
 * BEHAVIOR:
 *   - Uses useNotifications hook from admin domain
 *   - Provides unread count to context consumers
 *
 * RISKS:
 *   - NOTE: Creates dependency on admin domain - may cause circular dependencies
 *   - NOTE: Very minimal wrapper - may need additional notification features
 *   - NOTE: Error handling if useNotifications fails is not implemented
 *
 * @param root0 - Props object
 *
 * @param root0.children - Child components
 *
 * @returns NotificationProvider component
 *
 */
export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const notificationsHook = useNotifications as () => { unreadCount: number };
  const { unreadCount } = notificationsHook();

  return (
    <NotificationContext.Provider value={{ unread: unreadCount }}>
      {children}
    </NotificationContext.Provider>
  );
};
